import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { RootState } from "../../../redux/store";
import { useAppSelector } from "../../../redux/hooks";
import { adminRoutes } from "../../../routers/AdminRouters";
import { SubscriberFormErrors } from "../../../types";
import { useEditSubscriberMutation, useGetSegmentsQuery, useGetSubscriberByIdQuery, useGetTiersQuery } from "../../../services";
import CountryPhoneInput, { getAllCountryCodes } from "../../../components/CountryPhoneInput";


const SubscriberEdit: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { subscriberid } = useParams();
  const [phoneInput, setPhoneInput] = useState<string>("");

  const {
    data: subscriberDetail,
    isLoading: isSubscriberLoading,
    isError: isSubscriberError,
  } = useGetSubscriberByIdQuery(subscriberid as string, {
    skip: !subscriberid,
  });

  const [editSubscriber, { isLoading: isSaving }] = useEditSubscriberMutation();
  const {
    data: segmentsResponse,
  } = useGetSegmentsQuery();
  const {
    data: tiersResponse,
  } = useGetTiersQuery();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    segment: "",
    tier: "",
  });


  const [errors, setErrors] = useState<SubscriberFormErrors>({});
  const emailRegex = /^\S+@\S+\.\S+$/;

  // Hydrate form when subscriber detail loads
  useEffect(() => {
    if (!subscriberDetail) return;
    const s = (subscriberDetail as any).data ?? subscriberDetail;
    const phoneValue = s?.phone;
    const fullPhone = phoneValue !== undefined && phoneValue !== null ? String(phoneValue) : "";

    let countryCode = "+91";
    let phoneNumber = fullPhone;

    const dynamicCountryCodes = getAllCountryCodes();

    // Try to find a matching country code at the start of the string
    // Since it's numeric, we compare without the '+' prefix
    const found = dynamicCountryCodes
      .filter(c => c.value.startsWith("+")) // Ensure it has a + to strip
      .sort((a, b) => b.value.length - a.value.length) // Check longer codes first
      .find(c => fullPhone.startsWith(c.value.replace("+", "")));

    if (found) {
      countryCode = found.value;
      phoneNumber = fullPhone.substring(found.value.replace("+", "").length);
    }

    setFormData({
      name: s?.name ?? "",
      email: s?.email ?? "",
      countryCode,
      phone: phoneNumber,
      segment: s?.segment ?? "",
      tier: s?.tier ?? "",
    });
    setPhoneInput(phoneNumber);

  }, [subscriberDetail]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "");
      setPhoneInput(cleaned);
      setFormData((prev) => ({ ...prev, phone: cleaned }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if ((errors as any)[name])
      setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: SubscriberFormErrors = {};
    if (!String(formData.name || "").trim())
      newErrors.name = "Name is required";
    if (!String(formData.email || "").trim())
      newErrors.email = "Email is required";
    else if (!emailRegex.test(String(formData.email).trim()))
      newErrors.email = "Email is invalid";
    if (!String(phoneInput || "").trim()) newErrors.phone = "Phone is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !subscriberid) return;

    try {
      await editSubscriber({
        subscriberid,
        data: {
          name: String(formData.name).trim(),
          email: String(formData.email).trim(),
          phone: Number(`${formData.countryCode.replace("+", "")}${formData.phone.trim()}`) || 0,
          segment: String(formData.segment).trim(),
          tier: String(formData.tier).trim(),
        },

      } as any).unwrap();

      navigate(adminRoutes.subscriberList);
    } catch (err) {
      console.error("Edit subscriber failed:", err);
    }
  };

  // Sidebar compensation (unchanged)
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // no extra options needed for subscriber edit

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-black" : "bg-gray-50"
        } p-6`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-2xl font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-800"
            }`}
        >
          Edit Subscriber
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.subscriberList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Subscriber
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            Edit Subscriber
          </span>
        </div>
      </div>

      {/* Single form card (keeps existing card styling) */}
      <div
        className={`${theme === "dark"
            ? "bg-[#1E1E1E] border border-gray-800"
            : "bg-white border border-gray-200"
          } rounded-lg p-6`}
      >
        <div className="mb-6">
          <h2
            className={`text-base font-medium border-b pb-2 border-gray-700 ${theme === "dark" ? "text-white" : "text-gray-800"
              }`}
          >
            Edit Subscriber
          </h2>
        </div>

        {isSubscriberError && (
          <p className={theme === "dark" ? "text-red-400" : "text-red-600"}>
            Failed to load subscriber.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          {/* Row: Name, Email, Phone */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label
                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                  }`}
              >
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter here"
                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.name
                    ? "border border-red-500"
                    : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                  }`}
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter here"
                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.email
                    ? "border border-red-500"
                    : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                  }`}
              >
                Phone
              </label>
              <CountryPhoneInput
                countryCode={formData.countryCode}
                phoneNumber={formData.phone}
                onCountryCodeChange={(code) => setFormData((f) => ({ ...f, countryCode: code }))}
                onPhoneNumberChange={handleChange}
                error={errors.phone}
                placeholder="Enter here"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}

            </div>
            <div>
              <label
                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                  }`}
              >
                Segment
              </label>
              <select
                name="segment"
                value={formData.segment}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.segment
                  ? "border border-red-500"
                  : theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              >
                <option value="" disabled>Select Segment</option>
                {segmentsResponse?.map((segment: any) => (
                  <option key={segment._id} value={segment._id}>
                    {segment.name}
                  </option>
                ))}
              </select>
              {errors.segment && (
                <p className="text-red-500 text-xs mt-1">{errors.segment}</p>
              )}
            </div>
            <div>
              <label
                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                  }`}
              >
                Tier
              </label>
              <select
                name="tier"
                value={formData.tier}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.tier
                  ? "border border-red-500"
                  : theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              >
                <option value="" disabled>Select Tier</option>
                {tiersResponse?.map((tier: any) => (
                  <option key={tier._id} value={tier._id}>
                    {tier.name}
                  </option>
                ))}

              </select>
              {errors.tier && (
                <p className="text-red-500 text-xs mt-1">{errors.tier}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="px-8 py-1.5 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
            >
              {isSaving ? "Updating..." : "Update"}
            </button>
            <Link
              to={`${adminRoutes.subscriberList}`}
              className={`px-8 py-1.5 rounded-md font-medium ${theme === "dark"
                  ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
            >
              Not Now
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriberEdit;
